/**
 * Host control legality (FR-9.5/9.6 — "one goal = one action").
 *
 * The Host UI renders exactly ONE control per `shared` host command, and shows it
 * ONLY when that command is legal for the current phase/clue state. This module is
 * the single, unit-tested source of that legality so the UI can't drift into a
 * second path or an illegal-action button. It mirrors the server's own guards
 * (game-actor.ts) — the server remains the authority; this just keeps the surface
 * minimal and honest client-side.
 *
 * Judging a buzzed-in answer applies the score in that one action — there is no
 * separate "apply score" control here, by design.
 */

import { gamePhaseOf } from '@jeopardy/shared';
import type { GameState } from '@jeopardy/shared';

/** Every contextual host action the host surface can offer. */
export type HostAction =
  | 'start_game'
  | 'start_generation'
  | 'cancel_generation'
  | 'open_buzzer'
  | 'judge_correct'
  | 'judge_incorrect'
  | 'pause_timer'
  | 'resume_timer'
  | 'reset_timer'
  | 'reopen_buzzer'
  | 'dismiss_clue'
  | 'advance_round'
  | 'final_judge'
  | 'end_game';

/** Which host actions are legal right now. A `select_clue` is a board-cell action. */
export interface HostActionAvailability {
  /** Lobby: start a PACK game (LOBBY → IN_ROUND — FR-1.3/9.5/9.6, M12). */
  startGame: boolean;
  /**
   * Lobby: start dynamic generation (LOBBY → GENERATING). Legal only for a
   * DYNAMIC game with ≥1 player preference so a random player never triggers
   * spend (M12 §3, FR-9.5/9.6).
   */
  startGeneration: boolean;
  /** GENERATING: abort the in-flight agent and return to LOBBY (M12). */
  cancelGeneration: boolean;
  /** Normal-play actions, shown contextually. */
  openBuzzer: boolean;
  judge: boolean; // both correct + incorrect share this gate (a player is answering)
  /** Recovery actions (FR-4.9), grouped + visually distinct in the UI. */
  reopenBuzzer: boolean;
  dismissClue: boolean;
  resetTimer: boolean;
  pauseTimer: boolean;
  resumeTimer: boolean;
  /** Whether the host may pick a clue on the board (board is in play). */
  canSelectClue: boolean;
  /** Advance to the next round (during board play — FR-3.4). */
  advanceRound: boolean;
  /** Judge the current Final Jeopardy player (during FINAL_JUDGING — FR-6.4). */
  finalJudge: boolean;
  /** Ending the game is legal any time before GAME_OVER. */
  endGame: boolean;
}

/** Derive the legal host actions from authoritative game state (null = not loaded). */
export function hostActionAvailability(state: GameState | null): HostActionAvailability {
  const none: HostActionAvailability = {
    startGame: false,
    startGeneration: false,
    cancelGeneration: false,
    openBuzzer: false,
    judge: false,
    reopenBuzzer: false,
    dismissClue: false,
    resetTimer: false,
    pauseTimer: false,
    resumeTimer: false,
    canSelectClue: false,
    advanceRound: false,
    finalJudge: false,
    endGame: false,
  };
  if (!state) return none;

  const endGame = gamePhaseOf(state) !== 'GAME_OVER';

  // Auto-host (#91): there is no human host — the in-actor auto-host opens buzzers,
  // judges, and advances rounds itself, and a player taps Start (players also hold
  // board control + the Dispute vote). So the human "host" surface offers NO
  // flow-driving controls in AUTO. The one exception is DYNAMIC generation, a spend
  // action the creator still triggers (parity with M12), plus ending the game.
  if (state.hostMode === 'AUTO') {
    if (gamePhaseOf(state) === 'LOBBY') {
      const hasPrefs = Object.keys(state.lobby?.preferences ?? {}).length > 0;
      return {
        ...none,
        startGeneration: state.mode === 'DYNAMIC' && hasPrefs,
        endGame,
      };
    }
    if (gamePhaseOf(state) === 'GENERATING') {
      return { ...none, cancelGeneration: true, endGame };
    }
    return { ...none, endGame };
  }

  // Pre-play lobby (M12): exactly one host action, depending on the game mode.
  // PACK → "Start game" (LOBBY → IN_ROUND); DYNAMIC → "Generate & start" (LOBBY →
  // GENERATING), enabled only once ≥1 player has submitted category preferences so
  // a random player never triggers Bedrock spend (M12 §3, FR-9.5/9.6).
  if (gamePhaseOf(state) === 'LOBBY') {
    const hasPrefs = Object.keys(state.lobby?.preferences ?? {}).length > 0;
    return {
      ...none,
      startGame: state.mode === 'PACK',
      startGeneration: state.mode === 'DYNAMIC' && hasPrefs,
      endGame,
    };
  }

  // Generation in flight (M12): the ONE host action is cancelling it (→ LOBBY).
  if (gamePhaseOf(state) === 'GENERATING') {
    return { ...none, cancelGeneration: true, endGame };
  }

  const clue = state.jeopardy.clueState;
  const clueOpen = gamePhaseOf(state) === 'CLUE_OPEN' && clue !== undefined;

  // #192 (FR-4.10/9.6/9.7): a resolved clue is holding its end-of-clue reveal — the
  // board returns automatically after the hold, so the host has NO clue controls
  // here (offering open/judge/dismiss/reopen would be an illegal action on a clue
  // that's already resolved). Only ending the game stays available.
  if (clueOpen && clue!.status === 'RESOLVED') {
    return { ...none, endGame };
  }

  // Final Jeopardy judging: the ONE host action is judging the current player.
  if (gamePhaseOf(state) === 'FINAL_JUDGING') {
    return { ...none, finalJudge: true, endGame };
  }

  if (!clueOpen) {
    // During board play the host may pick a clue AND advance to the next round.
    // advance_round is only meaningful on a board round (not Final phases).
    const onBoard = gamePhaseOf(state) === 'IN_ROUND';
    return {
      ...none,
      canSelectClue: onBoard,
      advanceRound: onBoard,
      endGame,
    };
  }

  // Daily Double awaiting its wager: no buzzer, no judge yet — the uncoverer
  // wagers first. Recovery (dismiss) is still available.
  const isDailyDouble = clue!.mode === 'DAILY_DOUBLE';
  const awaitingWager = clue!.status === 'AWAITING_WAGER';
  const someoneAnswering = clue!.activePlayerId !== undefined;
  const buzzerOpen = clue!.status === 'BUZZER_OPEN';
  // #350: on a DD the host uses the same control to open the uncoverer's ANSWER
  // window after reading the clue — offered once the wager is locked (prompt
  // revealed, LOCKED) and nobody is answering yet (FR-5.5). HostPage labels it
  // "Open answer" for a DD; the command (open_buzzer) is the same.
  const ddReadyToAnswer =
    isDailyDouble &&
    clue!.status === 'LOCKED' &&
    clue!.wager !== undefined &&
    !someoneAnswering;
  // "Open buzzer" only for a normal clue, before buzzing, while nobody holds it.
  const openBuzzer =
    (!isDailyDouble && !buzzerOpen && !someoneAnswering) || ddReadyToAnswer;
  // Judge only when a player holds the buzzer / is answering (correct/incorrect
  // are the two outcomes of the ONE judge action, not two separate scoring paths).
  const judge = someoneAnswering;
  const timer = state.timer;
  return {
    ...none,
    openBuzzer,
    judge,
    // Recovery: reopen only makes sense for a buzzer clue (DD has no buzzing).
    reopenBuzzer: !isDailyDouble,
    // Dismiss ("nobody gets this") is available whenever a clue is open, except
    // a DD still awaiting its wager (nothing to dismiss yet — reopen not shown).
    dismissClue: !awaitingWager,
    resetTimer: !awaitingWager,
    pauseTimer: timer.isActive && !timer.isPaused,
    resumeTimer: timer.isActive && timer.isPaused,
    endGame,
  };
}
